/**
 * R14.3-B — Custom dark dropdown. Replaces native <select> wherever a
 * predictable cross-platform dark popup is required (user is on the
 * 4th report of the dropdown background being light).
 *
 * Why custom instead of OS-native <select>:
 *   - Chrome/Edge/Safari render the <select> popup as an OS-native widget.
 *     CSS `color-scheme: dark` makes it follow the system dark theme on
 *     Chromium, but on Firefox + mobile Safari the popup is still
 *     OS-light-themed for many users.
 *   - For DOM-rendered popups we get full control over background, hover,
 *     selected state, keyboard nav, and visual consistency.
 *
 * API:
 *   <DarkSelect
 *     value={camId}                          // current selection (string)
 *     onChange={(v) => setCamId(v)}          // change handler
 *     options={[{value, label, hint?}, ...]} // options list
 *     placeholder="Select camera"            // shown when value is empty/no match
 *     testid="pre-room-cam-select"           // optional test id for the trigger
 *     className="…"                          // extra trigger classes
 *     align="start"                          // "start" | "end"
 *   />
 *
 * Accessibility:
 *   - Trigger is a button with aria-haspopup="listbox" + aria-expanded.
 *   - Listbox has role="listbox", items role="option" with aria-selected.
 *   - Up/Down navigate options, Enter selects, Escape closes.
 *   - Closes on outside click + scroll.
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";

export default function DarkSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  testid,
  className = "",
  align = "start",
  disabled = false,
}) {
  const id = useId();
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const [rect, setRect] = useState(null);

  const current = options.find((o) => o.value === value);

  // Position the portal menu beneath the trigger.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const r = triggerRef.current.getBoundingClientRect();
      setRect({
        top: r.bottom + 4,
        left: align === "end" ? r.right : r.left,
        width: r.width,
        minWidth: 180,
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, align]);

  // Outside-click close.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Keyboard.
  const onKeyDown = useCallback((e) => {
    if (disabled) return;
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
        setFocusIdx(Math.max(0, options.findIndex((o) => o.value === value)));
      }
      return;
    }
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIdx((i) => Math.min(options.length - 1, (i < 0 ? 0 : i + 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIdx((i) => Math.max(0, (i < 0 ? 0 : i - 1)));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const o = options[focusIdx];
      if (o) { onChange?.(o.value); setOpen(false); }
    }
  }, [open, options, focusIdx, value, onChange, disabled]);

  const pick = (o) => {
    onChange?.(o.value);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-menu`}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onKeyDown}
        data-testid={testid}
        className={
          "w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm " +
          "bg-white/[0.04] hover:bg-white/[0.07] border border-white/12 " +
          "focus:outline-none focus:border-[#00F0FF]/50 " +
          "disabled:opacity-40 disabled:cursor-not-allowed " +
          "transition " + className
        }
      >
        <span className={"truncate text-left flex-1 " + (current ? "text-white" : "text-white/45")}>
          {current ? current.label : placeholder}
        </span>
        <ChevronDown className={"w-4 h-4 text-white/50 shrink-0 transition " + (open ? "rotate-180" : "")} />
      </button>

      {open && rect && createPortal(
        <ul
          ref={menuRef}
          id={`${id}-menu`}
          role="listbox"
          data-testid={testid ? `${testid}-menu` : undefined}
          className="fixed z-[9999] sr-dropdown-menu rounded-lg py-1 max-h-[60vh] overflow-y-auto"
          style={{
            top: rect.top,
            left: align === "end" ? undefined : rect.left,
            right: align === "end" ? window.innerWidth - rect.left : undefined,
            width: Math.max(rect.width, rect.minWidth),
            backgroundColor: "#0a0a14",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            boxShadow: "0 12px 36px rgba(0, 0, 0, 0.65)",
          }}
        >
          {options.length === 0 && (
            <li className="px-3 py-2 text-xs text-white/45 italic" data-testid={testid ? `${testid}-empty` : undefined}>
              No devices available — grant camera permission first.
            </li>
          )}
          {options.map((o, i) => {
            const selected = o.value === value;
            const focused = i === focusIdx;
            return (
              <li
                key={o.value}
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setFocusIdx(i)}
                onClick={() => pick(o)}
                data-testid={testid ? `${testid}-option-${i}` : undefined}
                className={
                  "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition " +
                  (focused
                    ? "bg-[#00F0FF]/15 text-white"
                    : selected
                      ? "bg-white/[0.04] text-white"
                      : "text-white/85")
                }
              >
                <span className="w-4 h-4 shrink-0 flex items-center justify-center">
                  {selected && <Check className="w-3.5 h-3.5 text-[#00F0FF]" />}
                </span>
                <span className="truncate flex-1">{o.label}</span>
                {o.hint && <span className="text-[10px] text-white/40 uppercase tracking-[0.14em]">{o.hint}</span>}
              </li>
            );
          })}
        </ul>,
        document.body,
      )}
    </>
  );
}
